import { format, parse } from 'date-fns'

export type DateInput = string | number | Date | null | undefined

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value === 'number') {
    const fromNumber = new Date(value)
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber
  }
  if (DATE_ONLY_PATTERN.test(value)) {
    const parsed = parse(value, 'yyyy-MM-dd', new Date())
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  const fromString = new Date(value)
  return Number.isNaN(fromString.getTime()) ? null : fromString
}

function toUtcCalendarDate(value: DateInput): Date | null {
  const date = toDate(value)
  if (!date) return null
  if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value)) return date
  const utcDay = new Date(2000, 0, 1)
  utcDay.setFullYear(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  return utcDay
}

export function formatDate(value: DateInput): string {
  const date = toDate(value)
  return date ? format(date, 'MMM d, yyyy') : ''
}

export function formatLongDate(value: DateInput): string {
  const date = toDate(value)
  return date ? format(date, 'MMMM d, yyyy') : ''
}

export function formatLongDateUtc(value: DateInput): string {
  const date = toUtcCalendarDate(value)
  return date ? format(date, 'MMMM d, yyyy') : ''
}

export function formatShortDate(value: DateInput): string {
  const date = toDate(value)
  return date ? format(date, 'MMM d') : ''
}

export function formatNumericDate(value: DateInput): string {
  const date = toDate(value)
  return date ? format(date, 'M/d/yyyy') : ''
}

export function formatDateTime(value: DateInput): string {
  const date = toDate(value)
  return date ? format(date, 'MMM d, yyyy h:mm a') : ''
}

export function formatShortDateTime(value: DateInput): string {
  const date = toDate(value)
  return date ? format(date, 'MMM d, h:mm a') : ''
}

export function formatNumericDateTime(value: DateInput): string {
  const date = toDate(value)
  return date ? format(date, 'M/d/yyyy, h:mm a') : ''
}

export function formatTime(value: DateInput): string {
  const date = toDate(value)
  return date ? format(date, 'h:mm a') : ''
}

export function formatMonthShort(value: DateInput): string {
  const date = toDate(value)
  return date ? format(date, 'MMM') : ''
}

export function formatMonthYear(value: DateInput): string {
  const date = toDate(value)
  return date ? format(date, 'MMMM yyyy') : ''
}

export function formatWeekdayShort(value: DateInput): string {
  const date = toDate(value)
  return date ? format(date, 'EEE') : ''
}

export function formatWeekdayLongDate(value: DateInput): string {
  const date = toDate(value)
  return date ? format(date, 'EEEE, MMMM d') : ''
}

export function formatWeekdayShortDate(value: DateInput): string {
  const date = toDate(value)
  return date ? format(date, 'EEEE, MMM d') : ''
}

export function formatWeekdayFullDate(value: DateInput): string {
  const date = toDate(value)
  return date ? format(date, 'EEEE, MMM d, yyyy') : ''
}

const CLOCK_TIME_PATTERN = /^(\d{1,2}):(\d{2})(?::\d{2})?$/

export function formatClockTime(value: string | null | undefined): string {
  if (!value) return ''
  const match = CLOCK_TIME_PATTERN.exec(value.trim())
  if (!match) return value
  const hour24 = Number.parseInt(match[1], 10)
  const minute = Number.parseInt(match[2], 10)
  if (hour24 > 24 || minute > 59) return value
  const reference = new Date(2000, 0, 1, hour24 % 24, minute)
  return format(reference, 'h:mm a')
}

export function formatDateRange(start: DateInput, end?: DateInput): string {
  const s = toDate(start)
  if (!s) return ''
  const e = toDate(end)
  if (!e) return formatDate(start)
  if (s.getFullYear() === e.getFullYear()) {
    return `${format(s, 'MMM d')} – ${format(e, 'MMM d, yyyy')}`
  }
  return `${format(s, 'MMM d, yyyy')} – ${format(e, 'MMM d, yyyy')}`
}
